import { useState } from 'react';

interface ClerkClientLike {
  destroy?: () => Promise<unknown>;
}

interface Props {
  client: unknown;
  signOut: (opts?: { redirectUrl?: string }) => Promise<void>;
}

// Shown when Clerk thinks the user is signed in but signOut cannot clear the
// session (orphan session: the Clerk user was deleted, so Clerk FAPI returns
// 401 on revocation and never emits the Set-Cookie that would clear __session).
// This component tries the nuclear route — destroying the entire Clerk Client
// via its DELETE endpoint — then falls back to manual site-data instructions.
export function StuckSessionModal({ client, signOut }: Props) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleReset() {
    setBusy(true);
    setFailed(false);
    try {
      const c = client as ClerkClientLike;
      if (c?.destroy) {
        await c.destroy().catch(() => {});
      }
      await signOut({ redirectUrl: '/' }).catch(() => {});
      localStorage.clear();
      sessionStorage.clear();
      setTimeout(() => {
        if (window.Clerk?.user) setFailed(true);
        else window.location.replace('/');
      }, 400);
    } catch (err) {
      console.warn('[stuck-session] reset failed:', err);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
        <h2 className="text-title font-bold">Session invalide</h2>
        <p className="text-body text-ink-soft">
          Votre session Clerk est orpheline (utilisateur supprimé ou recréé côté serveur).
          La déconnexion standard ne peut pas effacer le cookie. Cliquez sur « Réinitialiser »
          pour forcer la destruction du client Clerk.
        </p>
        {failed && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 text-body p-3 space-y-1">
            <p className="font-medium">Réinitialisation automatique impossible.</p>
            <p>
              Ouvre DevTools (F12) → Application → Storage → <em>Clear site data</em>, puis recharge la page.
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={handleReset}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-ink text-white text-body font-medium disabled:opacity-50"
          >
            {busy ? 'Réinitialisation…' : 'Réinitialiser la session'}
          </button>
        </div>
      </div>
    </div>
  );
}

declare global {
  interface Window {
    Clerk?: {
      user?: unknown;
      /**
       * Typé au strict nécessaire : `apiClient` appelle `getToken` pour
       * rejouer une requête 401 avec un JWT frais (cf. lib/apiClient.ts).
       */
      session?: {
        getToken?: (opts?: { skipCache?: boolean }) => Promise<string | null>;
      };
      client?: ClerkClientLike;
    };
  }
}
